import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { API_URL } from "../../../helpers/utils";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// --- Shared Interface for all Parts Restriction APIs ---
export interface PartsRestrictionFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string | null;
  sortOrder?: string | null;
}

interface PartsRestrictionAPIParams {
  filters: PartsRestrictionFilterParams;
  onSuccess: (data: any) => void;
  onError: () => void;
  signal?: AbortSignal;
}

// 1. Profile Height API (Existing)
export const getprofileHeightRestrctionsAPI =
  ({ filters, onSuccess, onError, signal }: PartsRestrictionAPIParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `${API_URL}/frame-parts/profile-height`,
        { params: filters, signal },
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch profile height list");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Fetch profile height error:", error);
      onError?.();
      message.error(error?.response?.data?.message || "Unable to fetch data");
    }
  };

// 2. Frame Depth & Bracing Length API
export const getFrameDepthRestrictionsAPI =
  ({ filters, onSuccess, onError, signal }: PartsRestrictionAPIParams) =>
  async (dispatch: any) => {
    try {
      // Endpoint matches Swagger: /frame-parts/frame-depth
      const response = await axiosInstance.get(
        `${API_URL}/frame-parts/frame-depth`,
        { params: filters, signal },
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch frame depth list");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Fetch frame depth error:", error);
      onError?.();
      message.error(error?.response?.data?.message || "Unable to fetch data");
    }
  };

// 3. Profile Width | Thickness | Max Height API
export const getProfileWidthRestrictionsAPI =
  ({ filters, onSuccess, onError, signal }: PartsRestrictionAPIParams) =>
  async (dispatch: any) => {
    try {
      // Endpoint matches Swagger: /frame-parts/profile-width
      const response = await axiosInstance.get(
        `${API_URL}/frame-parts/profile-width`,
        { params: filters, signal },
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch profile width list");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Fetch profile width error:", error);
      onError?.();
      message.error(error?.response?.data?.message || "Unable to fetch data");
    }
  };

// 4. Length & Width API
export const getLengthWidthRestrictionsAPI =
  ({ filters, onSuccess, onError, signal }: PartsRestrictionAPIParams) =>
  async (dispatch: any) => {
    try {
      // Endpoint matches Swagger: /frame-parts/length-width
      const response = await axiosInstance.get(
        `${API_URL}/frame-parts/length-width`,
        { params: filters, signal },
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch length & width list");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Fetch length & width error:", error);
      onError?.();
      message.error(error?.response?.data?.message || "Unable to fetch data");
    }
  };

interface ExportFramePartsParams {
  onSuccess?: () => void;
  onError?: () => void;
}

export const exportFramePartsAPI =
  ({ onSuccess, onError }: ExportFramePartsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `${API_URL}/frame-parts/export`,
        {
          responseType: "blob",
        },
      );

      // --- 1. EXTRACT FILENAME FROM HEADERS ---
      let fileName = "Frame Parts and Accessories.xlsx"; // Default Fallback

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        // Regex to find "filename=" value, handling quotes
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      // 2. Create a "Blob" from the binary data
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        // 3. Create a hidden link element to trigger the download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // 4. Set the dynamic filename
        link.setAttribute("download", fileName);

        // 5. Trigger click and cleanup
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("File downloaded successfully!");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      onError?.();

      // Helper to read the error message from a Blob
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to download file");
          } catch (e) {
            message.error("Failed to download file");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error("Failed to download file");
      }
    }
  };

interface ImportFramePartsParams {
  file: File;
  onSuccess: () => void;
  onError: () => void;
}

export const importFramePartsAPI =
  ({ file, onSuccess, onError }: ImportFramePartsParams) =>
  async (dispatch: any) => {
    try {
      const formData = new FormData();

      formData.append("file", file);

      const response = await axiosInstance.post(
        `${API_URL}/frame-parts/import`,
        formData,
        {
          headers: {
            // Essential for file uploads
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // 3. Handle Success
      // Note: Adjust 'response.data.success' based on your exact backend wrapper
      if (response.data?.success) {
        message.success(
          response.data?.message || "Frame parts imported successfully!",
        );
        onSuccess();
      } else {
        message.error(response.data?.message || "Import failed");
        onError();
      }
    } catch (error: any) {
      console.error("Import error:", error);

      const errorMessage =
        error?.response?.data?.message || "Import failed. Please try again.";
      message.error(errorMessage);
      onError();
    }
  };
